import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext(null);
// Use environment variable or hardcode the full API URL
// Modify to match the current hostname - could be localhost in development or actual domain in production
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    // Check if user is logged in on initial load
    const token = localStorage.getItem('token');
    const userJSON = localStorage.getItem('user');

    if (!token || !userJSON) {
      setLoading(false);
      return;
    }

    let user;
    try {
      user = JSON.parse(userJSON);
    } catch (err) {
      console.error("Failed to parse user data:", err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setLoading(false);
      return;
    }

    // Kiosk tokens are self-contained and bypass server refresh
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.type === 'kiosk') {
        console.log('🔧 AuthContext: Kiosk token detected, setting user without validation');
        console.log('🔧 AuthContext: Kiosk user data:', user);
        setCurrentUser(user);
        setLoading(false);
        return;
      }
    } catch (tokenError) {
      console.error('Error parsing token for kiosk check:', tokenError);
    }

    // Hydrate from localStorage so UI can render immediately
    setCurrentUser(user);
    if (user.force_password_reset) {
      setForcePasswordChange(true);
    }
    setLoading(false);

    // Refresh from server so any role/permission changes since last login take effect
    // (prevents stale localStorage from leaving admin-only UI visible for non-admins)
    if (user.id) {
      fetch(`${API_URL}/users/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
        .then(res => {
          if (res.status === 401 || res.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setCurrentUser(null);
            return null;
          }
          if (!res.ok) return null;
          return res.json();
        })
        .then(fresh => {
          if (!fresh) return;
          localStorage.setItem('user', JSON.stringify(fresh));
          setCurrentUser(fresh);
          if (fresh.force_password_reset) setForcePasswordChange(true);
        })
        .catch(err => {
          console.warn('AuthContext: failed to refresh currentUser on mount:', err);
        });
    }
  }, []);

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to login');
      }

      // Check if this is a force password reset response for client users
      if (data.requirePasswordReset && data.userType === 'client') {
        return {
          requirePasswordReset: true,
          userId: data.userId,
          username: data.username,
          userType: data.userType
        };
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      
      // Check if user needs to change password
      if (data.user && data.user.force_password_reset) {
        setForcePasswordChange(true);
      }
      
      return data;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred');
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to register');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      return data;
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'An unexpected error occurred');
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setForcePasswordChange(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          currentPassword, 
          newPassword,
          forceChange: forcePasswordChange
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      // If password was changed due to force_password_reset, update user in storage
      if (forcePasswordChange) {
        const updatedUser = { ...currentUser, force_password_reset: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setCurrentUser(updatedUser);
        setForcePasswordChange(false);
      }

      return data;
    } catch (err) {
      console.error('Password change error:', err);
      throw err;
    }
  };

  const updateCurrentUser = async () => {
    try {
      // Only proceed if the user is logged in
      if (!currentUser || !currentUser.id) {
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await fetch(`${API_URL}/users/${currentUser.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const userData = await response.json();
      
      // Update local storage and state
      localStorage.setItem('user', JSON.stringify(userData));
      setCurrentUser(userData);
      
      // Check if user needs to change password
      if (userData.force_password_reset) {
        setForcePasswordChange(true);
      }

      return userData;
    } catch (err) {
      console.error('Error updating user data:', err);
      throw err;
    }
  };

  const changePasswordFirstLogin = async (userId, currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/client-users/change-password-first-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to change password');
      }

      // Store the token and user data after successful password change
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setCurrentUser(data.user);
      setForcePasswordChange(false);

      return data;
    } catch (err) {
      console.error('Client password change error:', err);
      setError(err.message || 'An unexpected error occurred');
      throw err;
    }
  };

  const value = {
    currentUser,
    login,
    register,
    logout,
    changePassword,
    changePasswordFirstLogin,
    updateCurrentUser,
    loading,
    error,
    forcePasswordChange,
    setForcePasswordChange,
    isAuthenticated: !!currentUser,
    authLoading: loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthContext;
